import { useEffect } from "react";
import { useUser } from "@/context/UserContext";
import { useTenant } from "@/context/TenantContext";

const DEFAULT_BRAND = "#16A34A";
const DEFAULT_FAVICON = "/icons/app-icon-64.png"; // el mismo que index.html
const STATIC_TITLE = "COLYGON Mobility";

/**
 * useTenantTheme — Iter 33.
 *
 * Listens to (TenantContext override || authenticated user's tenant) and:
 *   - Applies `--brand-primary` to the :root CSS variable so any component
 *     reading `var(--brand-primary)` re-paints instantly.
 *   - Replaces the <link rel="icon"> href so the browser tab shows the active
 *     tenant favicon. Falls back to /favicon.ico when not configured.
 *
 * Restores defaults when no tenant context is active (logged-out, etc.).
 */
export const useTenantTheme = () => {
  const { user } = useUser();
  const { tenantOverride } = useTenant();

  useEffect(() => {
    const color =
      tenantOverride?.primary_color ||
      user?.company_primary_color ||
      DEFAULT_BRAND;
    const favicon =
      tenantOverride?.favicon_url ||
      user?.company_favicon_url ||
      DEFAULT_FAVICON;

    if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(color)) {
      document.documentElement.style.setProperty("--brand-primary", color);
    } else {
      document.documentElement.style.setProperty("--brand-primary", DEFAULT_BRAND);
    }

    // Iter 33 — enforce the static product title; nothing should override it.
    if (document.title !== STATIC_TITLE) document.title = STATIC_TITLE;

    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = favicon;

    return () => {
      // Reset on unmount (only the very last consumer).
      // We don't aggressively revert here — the next useEffect cycle will
      // re-apply on tenant change anyway.
    };
  }, [tenantOverride, user]);
};

export default useTenantTheme;
